import React from 'react';
import { useTranslation } from 'react-i18next';
import { sensorRating, getPillStatusFromRating } from 'commons/src/commonFunctions';
import StatusPill from 'commons/src/components/pills/StatusPill';
import SensorIcon from 'commons/src/components/sensors/SensorIcon';
import { sensorUnits } from 'commons/src/constants';
import { Rating, SensorTypes } from 'commons/src/models/commonEnums';
import { ReportSectionWrapper } from '../../../../../components/wrappers';
import { IaqInsightData, ReportSensorThresholds } from '../../../../../models/common';
import styles from '../../iaqInsight2.module.scss';

export type AverageMeasurementsDuringOpeningHoursProps = {
    sensorReports: { [sensor: string]: IaqInsightData };
    thresholds: { [sensor: string]: ReportSensorThresholds };
};

type SensorAverageComponentProps = {
    sensor: SensorTypes;
    sensorReports: { [sensor: string]: IaqInsightData };
    thresholds: { [sensor: string]: ReportSensorThresholds };
};

export const SensorAverageComponent = (props: SensorAverageComponentProps): React.ReactElement | null => {
    const { sensor, sensorReports, thresholds } = props;
    const { t: txt } = useTranslation();

    const sensorDetails: IaqInsightData | undefined = sensorReports[sensor];
    const sensorThreshold = thresholds[sensor];

    if (!sensorDetails || !sensorThreshold) {
        return null;
    }

    const value = sensorDetails.averageValueWithinOpeningHours;
    const baseUnit = sensorUnits[sensorThreshold.unit];

    let displayedUnit = baseUnit;
    if (sensorThreshold.unit === 'c') {
        displayedUnit = `${baseUnit}C`;
    } else if (sensorThreshold.unit === 'f') {
        displayedUnit = `${baseUnit}F`;
    }

    const hasValue = value !== undefined && value !== null && !Number.isNaN(value);
    let formattedValue: string | null = null;
    if (hasValue) {
        formattedValue = String(value);
    }
    const displayAsZero = formattedValue === '0' || formattedValue === '0.0';
    const sensorHasValidValue = hasValue && !displayAsZero;

    const valueRange: Rating | undefined = sensorHasValidValue
        ? sensorRating(sensorThreshold.ranges, value)
        : undefined;
    const pillStatus = sensorHasValidValue ? getPillStatusFromRating(valueRange) : undefined;

    return (
        <div key={`${sensor}-average`} className={styles.sensorContainer}>
            <SensorIcon sensor={sensor} />
            <div>
                <div className={styles.sensorInfo}>
                    <span className={styles.sensorLabel}>{txt(sensor)}</span>
                    {pillStatus && <StatusPill status={pillStatus} outline size="small" />}
                </div>
                <span className={styles.sensorValue}>
                    {sensorHasValidValue && formattedValue ? (
                        <>
                            {formattedValue}
                            <span className={styles.sensorUnit}>{displayedUnit}</span>
                        </>
                    ) : (
                        txt('IAQReport2.NotAvailable')
                    )}
                </span>
            </div>
        </div>
    );
};

const AverageMeasurementsDuringOpeningHours: React.FC<AverageMeasurementsDuringOpeningHoursProps> = ({
    sensorReports,
    thresholds,
}) => {
    const { t: txt } = useTranslation();

    if (!sensorReports || !thresholds || typeof sensorReports !== 'object' || typeof thresholds !== 'object') {
        return null;
    }

    const sensorReportArray = Object.keys(sensorReports).filter(sensor => {
        const report = sensorReports[sensor];
        const threshold = thresholds[sensor];

        if (!report || !threshold) {
            return false;
        }

        return typeof report === 'object' && typeof threshold === 'object';
    });

    if (sensorReportArray.length === 0) {
        return null;
    }

    return (
        <ReportSectionWrapper className={styles.averageMeasurementsWrapper}>
            <h3 className={styles.sectionTitle}>{txt('IAQReport2.AirQualityOverview')}</h3>
            <p className={styles.airQualityAveragesDescription}>{txt('IAQReport2.AirQualityAveragesDescription')}</p>
            <div className={styles.sensorGrid}>
                {sensorReportArray
                    .sort((sensorA, sensorB) => sensorA.localeCompare(sensorB))
                    .map(sensor => (
                        <SensorAverageComponent
                            key={`building-${sensor}-average`}
                            sensor={sensor as SensorTypes}
                            sensorReports={sensorReports}
                            thresholds={thresholds}
                        />
                    ))}
            </div>
        </ReportSectionWrapper>
    );
};

export default AverageMeasurementsDuringOpeningHours;
